import { Suspense, useEffect, useState } from "react";
import ServicesPageSkeleton from "@/app/booking/service/service-page-skeleton";
import { useRouter, useSearchParamsCompat } from "@/lib/router-helpers";
import ProgressPill from "@/components/progress-pill"
import ServicesGridContainer from "@/components/services-grid-container"
import { CarFront, Clock, ArrowLeft } from "lucide-react"
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom"
import { useAuth } from "@/components/auth-provider";
import { useBookingStore } from "@/store/booking-store"

function SelectServicePage() {
  const [searchParams] = useSearchParamsCompat()
  const router = useRouter()
  const { selectedBranch, setBookingState, bookingType } = useBookingStore()
  const { isAuthenticated, isLoading } = useAuth()
  
  useEffect(() => {
    // Check authentication on mount
    if (!isLoading && !isAuthenticated) {
        // Construct pending booking object to restore state after login
        const bId = searchParams?.get("branch_id")
        const pendingBooking = {
            selectedBranch: bId ? { 
                id: bId, 
                name: searchParams?.get("branch_name") || "",
                subtitle: searchParams?.get("branch_subtitle") || ""
            } : selectedBranch,
            mode: (searchParams?.get("mode") as any) || "booking",
            appointmentId: searchParams?.get("appointment_id"),
            bookingType: bookingType
        }

        if (pendingBooking.selectedBranch) {
             window.sessionStorage.setItem("qetero.pendingBooking", JSON.stringify(pendingBooking))
        }
       
        const targetUrl = window.location.pathname + window.location.search
        router.push(`/auth/verify?returnUrl=${encodeURIComponent(targetUrl)}`)
    }
  }, [isLoading, isAuthenticated, router, searchParams, selectedBranch, bookingType]);

  useEffect(() => {
    if (!selectedBranch) {
      // 1. Check for pending booking from auth flow (sessionStorage)
      const pending = window.sessionStorage.getItem("qetero.pendingBooking")
      if (pending) {
        try {
          const data = JSON.parse(pending)
          setBookingState(data)
          window.sessionStorage.removeItem("qetero.pendingBooking") // Clear so refresh works as expected later
          return
        } catch (e) {
          console.error("Failed to parse pending booking", e)
        }
      }

      // 2. Fallback to URL params (for deep links)
      const bId = searchParams?.get("branch_id")
      if (bId) {
        setBookingState({
          selectedBranch: {
            id: bId,
            name: searchParams?.get("branch_name") || "",
            subtitle: searchParams?.get("branch_subtitle") || ""
          },
          mode: (searchParams?.get("mode") as any) || "booking",
          appointmentId: searchParams?.get("appointment_id")
        })
      } else {
        router.push("/")
      }
    }
  }, [selectedBranch, searchParams, router, setBookingState])

  const branchId = selectedBranch?.id || ""
  const branchName = selectedBranch?.name || ""
  const branchSubtitle = selectedBranch?.subtitle || ""
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    const checkStatus = () => {
      const now = new Date();
      const hour = now.getHours();
      const minute = now.getMinutes();
      const day = now.getDay();
      
      const isExtended = branchName === "Ledeta" || branchName === 'Megenagna' || branchName === "VIP-Megenagna";
      const isSaturday = day === 6;

      let openHour = 8;
      let openMinute = 30; // Default 8:30 for others

      if (isExtended) {
        if (isSaturday) {
          openHour = 8;
          openMinute = 30;
        } else {
          openHour = 8;
          openMinute = 0;
        }
      }

      const closingHour = isExtended || !isSaturday ? 19 : 17;
      
      const nowMinutes = hour * 60 + minute;
      const openMinutes = openHour * 60 + openMinute;
      const closeMinutes = closingHour * 60;

      setIsOpen(nowMinutes >= openMinutes && nowMinutes < closeMinutes);
    };
    checkStatus();
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, [branchName]);

  const mode = searchParams?.get("mode")
  const appointmentId = searchParams?.get("appointment_id")

  let backLink = "/organization/dars"
  if (mode === "reschedule" && appointmentId) {
    const params = new URLSearchParams()
    params.append("mode", mode)
    params.append("appointment_id", appointmentId)
    backLink = `/organization/dars?${params.toString()}`
  }
  if (isLoading) {
      return <ServicesPageSkeleton />
    }

  return (
    <>
    <style>{`
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-fade-in-up {
        animation: fadeInUp 0.5s ease-out forwards;
      }
    `}</style>
    <div className="w-full  mx-auto md:px-4 md:pb-20 pt-2 font-sans animate-fade-in-up">
      <div className="hidden md:block mb-6">
        <ProgressPill value={40} />
      </div>

      {/* Branch card */}
      <div 
        className="rounded-3xl border border-gray-100 bg-white py-4 md:p-5 mb-3 md:mb-6 shadow-sm relative overflow-hidden group"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full blur-3xl -mr-10 -mt-10 transition-all duration-500 group-hover:bg-pink-100" />
        
        <div className="relative z-10 flex flex-col items-center gap-3 text-center">
          <div className="flex items-center gap-4">
            <div className="md:w-12 md:h-12 md:rounded-full md:bg-pink-50 flex items-center justify-center mb-1">
               <img src="/icon/branch.svg" alt="branch" className="w-6 h-6" />
            </div>
            
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                {t(branchName) || t("Selected Branch")}
              </h1>
            </div>
          </div>
              {branchSubtitle && (
                <p className="text-sm text-gray-500 md:mt-1 font-medium">
                  {t(branchSubtitle)}
                </p>
              )}

          <div className="flex flex-wrap justify-center items-center gap-3 text-xs mt-2 bg-gray-50 py-2 px-4 rounded-full border border-gray-100">
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${isOpen ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
              <span className={`${isOpen ? "text-green-600" : "text-red-600"} font-bold`}>{isOpen ? t("Open") : t("Closed")}</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-gray-200" />
            <div className="flex items-center gap-1.5 text-gray-500">
              <Clock className="w-3.5 h-3.5" />
              <span>{t('Closes')} {(branchName === "Ledeta" || branchName === 'Megenagna' || branchName === "VIP-Megenagna") ? '1:00': '11:00'}</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-gray-200" />
            <div className="flex items-center gap-1.5 text-gray-500">
              <CarFront className="w-3.5 h-3.5" />
              <span>__ {t("min")}</span>
            </div>
          </div>
        </div>
      </div>

      <div 
        className="rounded-3xl bg-white shadow-sm border border-gray-100 p-2 md:p-6 relative overflow-hidden min-h-[400px]"
      >
        {/* Decorative background blob */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 pointer-events-none" />

        <div className="flex items-center justify-between mb-6 relative z-10">
          <h2 className="text-lg font-bold text-gray-900">{t("selectService")}</h2>
          
        </div>

        <ServicesGridContainer branchId={branchId} branchName={branchName} branchSubtitle={branchSubtitle} />
      </div>

      <div 
        className="flex justify-start mt-8"
      >
        <Link 
          to={backLink}
          className="group px-8 h-12 flex items-center gap-2 rounded-full border border-gray-200 bg-white text-gray-900 text-sm font-semibold hover:border-pink-500 hover:text-pink-600 transition-all duration-300 shadow-sm hover:shadow-md active:scale-95"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          {t("back")}
        </Link>
      </div>
    </div>
    </>
  )
}

export default function BookingServicePage() {
  return (
   <Suspense fallback={<ServicesPageSkeleton />}>
     <SelectServicePage/>
   </Suspense>
  )
}
